import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Team } from '../models/team.model';

/**
 * Team Context Service
 * Manages the currently selected team context for the application
 */
@Injectable({
  providedIn: 'root'
})
export class TeamContextService {
  private selectedTeamSubject = new BehaviorSubject<Team | null>(null);
  public selectedTeam$: Observable<Team | null> = this.selectedTeamSubject.asObservable();

  private readonly STORAGE_KEY = 'selected_team';

  constructor() {
    this.loadSelectedTeam();
  }

  /**
   * Get current selected team
   */
  getSelectedTeam(): Team | null {
    return this.selectedTeamSubject.value;
  }

  /**
   * Set selected team
   */
  setSelectedTeam(team: Team | null): void {
    this.selectedTeamSubject.next(team);
    if (team) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(team));
    } else {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * Clear selected team
   */
  clearSelectedTeam(): void {
    this.setSelectedTeam(null);
  }

  /**
   * Load selected team from storage
   */
  private loadSelectedTeam(): void {
    const storedTeam = localStorage.getItem(this.STORAGE_KEY);
    if (storedTeam) {
      try {
        const team = JSON.parse(storedTeam);
        this.selectedTeamSubject.next(team);
      } catch (error) {
        console.error('Error loading selected team:', error);
        localStorage.removeItem(this.STORAGE_KEY);
      }
    }
  }

  /**
   * Check if a team is selected
   */
  hasSelectedTeam(): boolean {
    return this.selectedTeamSubject.value !== null;
  }

  /**
   * Require a team to be selected, throw error if not
   */
  requireSelectedTeam(): Team {
    const team = this.getSelectedTeam();
    if (!team) {
      throw new Error('No team selected. Please select a team first.');
    }
    return team;
  }
}
